import { useNavigate } from '@tanstack/react-router'
import { CookieStorage } from 'cookie-storage'
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import React, { useCallback, useEffect, useState } from 'react'

import { hasLoginAuthParams } from './helper'
import {
  KeycloakAuthenticatedContent,
  KeycloakMessageBanner,
} from './KeycloakLoginScreen'

const KeycloakContext = React.createContext<IKeycloakContext>(
  {} as IKeycloakContext
)
export const useKeycloak = () => {
  const context = React.useContext(KeycloakContext)
  if (!context) {
    throw new Error('useKeycloak must be used within KeycloakProvider')
  }
  return context
}

type IKeycloakContext = {
  isAuthenticated: boolean
  isLoading: boolean
  error: Error | null
  user: User | null
  sendToKeycloakLogin: (scopes?: string[]) => void
  signOut: () => void
  /* Function that attempts to exchange the currently stored refresh token for a fresh acceess token.
   * Returns a promise that is fulfilled the refresh was successful, rejected if unsuccesful, and undefined if the
   * global user manage has not been instantiated yet*/
  exchangeRefreshToken: () => Promise<void> | undefined
}

type KeycloakProviderOptions = {
  children: React.JSX.Element
  clientId: string
  keycloakDomain: string
  keycloakRealm: string
  basePath?: string
}

type AppState = {
  redirectLocation?: string
}

// We store this in a global to prevent multiple user managers from being
// created simultaneously, which causes problems.
let globalKeycloakUserManager: UserManager | undefined = undefined

export const KeycloakProvider = ({
  children,
  clientId,
  keycloakDomain,
  keycloakRealm,
  basePath = '',
}: KeycloakProviderOptions) => {
  const [user, setUser] = useState<User | null>(null)
  const [error, setError] = useState<Error | null>(null)
  // `isLoading` should be true while we're handling callback url params. It
  // should only be set to false once we're done. We rely on this in the
  // `KeycloakAuthenticatedContent` component, which can do the wrong thing if
  // `isLoading` is set to false without `isAuthenticated` being set properly.
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [cookieStore] = useState<CookieStorage>(
    new CookieStorage({
      expires: new Date(Date.now() + 60 * 60 * 1000),
      path: basePath ? basePath : '/',
    })
  )

  const navigate = useNavigate()

  useEffect(() => {
    // Only initialize the user manager once. If we run this multiple times, we
    // end up with concurrently running user managers and bizarre problems
    if (globalKeycloakUserManager) {
      return
    }
    globalKeycloakUserManager = new UserManager({
      authority: `${keycloakDomain}/realms/${keycloakRealm}/account`,
      client_id: clientId,
      redirect_uri: window.location.origin + basePath,
      metadataUrl: `${keycloakDomain}/realms/${keycloakRealm}/.well-known/openid-configuration`,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      stateStore: new WebStorageStateStore({ store: cookieStore }),
      automaticSilentRenew: false,
    })
    globalKeycloakUserManager.events.addUserLoaded((user) => {
      setUser(user)
    })

    globalKeycloakUserManager.events.addUserUnloaded(() => {
      setUser(null)
    })

    if (hasLoginAuthParams(window.location.search)) {
      // If the current path is the callback, process and redirect back to the original location
      globalKeycloakUserManager
        .signinRedirectCallback(window.location.href)
        .then((user) => {
          globalKeycloakUserManager?.storeUser(user)
          const state = user.state as AppState
          const returnTo = state.redirectLocation || window.location.href

          try {
            const url = new URL(returnTo)
            const pathname = url.pathname.replace(basePath, '')
            const urlSearchParams = new URLSearchParams(url.search)
            const searchParams: Record<string, string | number | boolean> = {}

            // Parse URL parameters and preserve types (boolean, number, string)
            for (const [key, value] of urlSearchParams.entries()) {
              // Convert "true"/"false" strings to boolean
              if (value.toLowerCase() === 'true') {
                searchParams[key] = true
              } else if (value.toLowerCase() === 'false') {
                searchParams[key] = false
              }
              // Convert numeric strings to numbers
              else if (/^-?\d+(\.\d+)?$/.test(value)) {
                searchParams[key] = Number(value)
              }
              // Keep everything else as string
              else {
                searchParams[key] = value
              }
            }

            navigate({
              to: pathname,
              search: searchParams,
              replace: true,
            })
          } catch (parseError) {
            console.error(
              'Error parsing url redirecting to home page',
              parseError
            )
            navigate({ to: returnTo, replace: true })
          }
        })
    } else {
      globalKeycloakUserManager.getUser().then((user: User | null) => {
        if (user) {
          setUser(user)
        }
        setIsLoading(false)
      })
    }
  }, [basePath, cookieStore, navigate, keycloakDomain, keycloakRealm, clientId])

  const sendToKeycloakLogin = async (
    scopes: string[] = ['email', 'openid', 'profile']
  ) => {
    setIsLoading(true)

    globalKeycloakUserManager
      ?.signinRedirect({
        scope: scopes.join(' '),
        redirect_uri: window.location.origin + basePath,
        state: { redirectLocation: window.location.href },
      })
      .catch((err) => {
        console.error(err)
        setError(err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const exchangeRefreshToken = useCallback(
    () =>
      globalKeycloakUserManager
        ?.signinSilent()
        .then((newUser) => setUser(newUser)),
    []
  )

  const signOut = async () => {
    setIsLoading(true)

    globalKeycloakUserManager
      ?.signoutRedirect({
        extraQueryParams: {
          client_id: clientId,
          post_logout_redirect_uri: `${window.location.origin}${basePath}`,
        },
      })
      .catch((err) => {
        console.error(err)
        setError(err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <KeycloakContext.Provider
      value={{
        isAuthenticated: !!user?.access_token,
        user,
        error,
        isLoading,
        signOut,
        sendToKeycloakLogin,
        exchangeRefreshToken,
      }}>
      <KeycloakAuthenticatedContent>{children}</KeycloakAuthenticatedContent>
      <KeycloakMessageBanner />
    </KeycloakContext.Provider>
  )
}
